import { createContext, Dispatch, SetStateAction, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Container, SelectPicker, IconButton, Input, InputGroup } from 'rsuite'
import SaveIcon from '@rsuite/icons/Check'
import CancelIcon from '@rsuite/icons/Close'
import CodeIcon from '@rsuite/icons/Code'
import { t } from 'i18next'
import useQuery from '@/client/utils/useQuery'
import ConverterListModel from '@/client/models/converter'
import { PanelError } from '@/client/components/ErrorProvider'
import { Button, Table } from '@/client/components/Controls'
import { syncTranslation } from '@/client/utils/translate'
import Loading from '@client/components/Loading'
import BreadCrumb from '@/client/components/Layout/BreadCrumb'
import { getClassMethods, getClassMethodBodies } from '@/shared/parseTypeScript'
import { Capabilities, hasPermission } from '@/shared'
import project from '@client/stores/projectStore'
import { SQLQueryBuilder } from './SQLQueryBuilder'
import SvgLoader from '@/client/components/SvgLoader'
import CodeEditor from './CodeEditor'
import { SettingsIcon, QueryIcon, SearchIcon } from '@/client/config'
import ConverterSettings from './ConverterSettings'
import { findTab } from '@client/stores/tabsStore'
import { useSyncedState } from '@client/utils/useSyncedState'
import { useEditLock } from '@client/socket/useEditLock'
import { user } from '@client/stores/authStore'
import toastMessage from '@client/utils/toastMessage'
import { BuildInfoType, getBuildInfo } from '@/client/config/constants'
import RenameFunctionsDialog from './RenameFunctionsDialog'
import { buildRenameOffers, TMethodSnapshot, TRenameOffer } from './renameOffers'
import { beginRowEdit, cancelRowEdit, endRowEdit } from '@client/utils/rowEdit'
import { filterRefusals, queryRefusal } from '@shared/queryFilter'

/** What an edit of a mapping row can change, and so what cancel has to put back. */
const MAPPING_EDIT_FIELDS = ['type', 'source', 'display'] as const

type classMethodsDataProps = { value: string; label: string }
type EditConverterListProps = { tabId: any }
type EditableCellProps = {
    rowData?: any
    dataKey?: any
    onChange?: any
    inTableData?: any
    maximized?: boolean
    setMaximized?: (moximized: boolean) => void
    //classMethods?: MultiCascadeTreeProps<string>[]
    className?: string
    fullText?: boolean
}
type EditableSourceCellProps = EditableCellProps & {
    //classMethods: ItemDataType<string>[]
}
type ActionCellProps = { rowData?: any; dataKey?: any; onClick?: any; className?: string }
type LabelValueProps = { label: string; value: string }

/** One offer for one target field, as routes/convert returns it. */
type TSuggestion = {
    target: string
    targetLabel: string
    best: { source: string; sourceLabel: string; rank: 'name' | 'own' | 'shared'; hits: number } | null
    ambiguous: boolean
    candidates: { source: string; sourceLabel: string }[]
}

/**
 * Why this is being offered.
 *
 * Shown on every suggestion, because a proposal nobody can check is a proposal
 * nobody should accept — the mapping decides which data lands in which column
 * of the export, and a mix-up surfaces in the target system, not here.
 */
const suggestionReason = (suggestion: TSuggestion): string => {
    if (!suggestion.best) return ''
    if (suggestion.best.rank === 'name') return t('Same name, written differently')

    return t('Assigned this way {count} times before').replace('{count}', String(suggestion.best.hits))
}

const converterListModel = new ConverterListModel()

// outside of components we have to sync the translations by our self
let converterTypes: classMethodsDataProps[] = []
let converterTypesSearchOptions: classMethodsDataProps[] = []
syncTranslation(() => {
    converterTypes = [
        { value: '', label: t('Not assigned') },
        { value: 'source', label: t('Source field') },
        { value: 'fixed-value', label: t('Fixed value') },
        { value: 'function', label: t('Function') },
        //{ value: 'inline-function', label: t('Inline') },
        //{ value: 'global-function', label: t('Global function') },
    ]
    converterTypesSearchOptions = [
        { value: 'all', label: t('All') },
        { value: 'not-assigned', label: t('Not assigned') },
        { value: 'source', label: t('Source field') },
        { value: 'fixed-value', label: t('Fixed value') },
        { value: 'function', label: t('Function') },
        //{ value: 'inline-function', label: t('Inline') },
        //{ value: 'global-function', label: t('Global function') },
    ]
    //console.log(converterTypes)
})

type EditorContextValue = {
    tab: any
    data: any
    /**
     * Whether this session may change anything.
     *
     * Permission and edit lock in one: the converter is held by one person at a
     * time (see the lock below), and everybody else reads. Both answers look
     * the same to a control, so they are decided in one place.
     */
    canEdit: boolean
    /**
     * Whether this session may save the converter's CODE.
     *
     * Not the same question as canEdit: assigning a column and writing code that
     * runs in the server process are different rights (Capabilities.convert_write
     * vs converter_code_write). A ProjectMember holds the first and not the
     * second — and used to be shown a Save button that could only ever answer
     * 403.
     */
    canEditCode: boolean
    /** What the memory offers for the target fields still empty, by column key. */
    suggestions: Record<string, TSuggestion>
    /** Takes the offer for one row and saves it like any other assignment. */
    applySuggestion: (rowId: string) => void
    /** Takes every offer at once, filling gaps only. */
    applyAllSuggestions: () => void
    classMethods: string[]
    classMethodsData: LabelValueProps[]
    /**
     * The mapping rows as they currently stand.
     *
     * The one place that knows what the mapping says. `data.mapping` is the
     * server's answer from the moment the converter was loaded and is never
     * written again — anything reading it for the current state is reading a
     * photograph.
     */
    rowData: any[]
    openSettings: boolean
    setOpenSettings: Dispatch<SetStateAction<boolean>>
    setSettingsHasChanged: Dispatch<SetStateAction<number>>
    openEditor: boolean
    setOpenEditor: Dispatch<SetStateAction<boolean>>
    setEditorDataHasChanged: Dispatch<SetStateAction<number>>
    handleEditorValidation: () => boolean
    /**
     * Saves the editor code, then checks whether it left mapping rules pointing
     * at functions that no longer exist. Saving never waits for that answer.
     */
    saveCode: () => void
    openSqlQueryBuilder: boolean
    setOpenSqlQueryBuilder: Dispatch<SetStateAction<boolean>>
    setSqlQueryBuilderHasChanged: Dispatch<SetStateAction<number>>
    /** a save of the query builder was refused: the rows show why */
    builderTried: boolean
    setBuilderTried: Dispatch<SetStateAction<boolean>>
    handleSave: (type: string, data: any) => void
}

const EditorContext = createContext<EditorContextValue>({
    tab: undefined,
    data: undefined,
    // Nothing until the lock says otherwise — a default of true would hand out
    // an editable screen for the moment before the answer arrives.
    canEdit: false,
    canEditCode: false,
    suggestions: {},
    applySuggestion: function (rowId: string) { },
    applyAllSuggestions: function () { },
    classMethods: [],
    classMethodsData: [],
    rowData: [],
    openSettings: false,
    setOpenSettings: function (value: SetStateAction<boolean>) { },
    setSettingsHasChanged: function (value: SetStateAction<number>) { },
    openEditor: false,
    setOpenEditor: function (value: SetStateAction<boolean>) { },
    setEditorDataHasChanged: function (value: SetStateAction<number>) { },
    handleEditorValidation: function () {
        return false
    },
    saveCode: function () { },
    openSqlQueryBuilder: false,
    setOpenSqlQueryBuilder: function (value: SetStateAction<boolean>) { },
    setSqlQueryBuilderHasChanged: function (value: SetStateAction<number>) { },
    builderTried: false,
    setBuilderTried: function (value: SetStateAction<boolean>) { },
    handleSave: function (type: string, data: any) { },
})

export function useConverter() {
    return useContext(EditorContext)
}

const EditableTypeCell = ({ rowData, dataKey, onChange, className = '', ...props }: EditableCellProps) => {
    const editing = rowData.status === 'EDIT'

    if (className) className += ' '
    className += editing ? 'table-content-editing' : ''

    const getLabel = (type: string) => {
        for (let i = 0; i < converterTypes.length; i++) {
            if (converterTypes[i].value == type) {
                return converterTypes[i].label
            }
        }
        return converterTypes[0].label
    }

    return (
        <Table.Cell {...props} className={className}>
            {editing ? (
                <SelectPicker
                    defaultValue={rowData[dataKey]}
                    data={converterTypes}
                    cleanable={false}
                    searchable={false}
                    placeholder={t('Select type ...')}
                    onSelect={(value) => {
                        onChange && onChange(rowData._id, dataKey, value)
                    }}
                />
            ) : (
                <span className="table-content-edit-span">{getLabel(rowData[dataKey])}</span>
            )}
        </Table.Cell>
    )
}
const EditableSourceCell = ({
    rowData,
    dataKey,
    onChange,
    inTableData,
    maximized,
    setMaximized,
    className = '',
    fullText = false,
    ...props
}: EditableSourceCellProps) => {
    const { classMethods, classMethodsData, suggestions, applySuggestion, canEdit } = useConverter()
    const editing = rowData.status === 'EDIT'
    const type = rowData.type || ''
    const suggestion = suggestions[String(rowData.target)]

    if (className) className += ' '
    className += editing ? 'table-content-editing' : ''

    const handleChange = (key: string, value: string) => {
        rowData.edit[key] = value
    }

    const getLabel = (column: string) => {
        if (column) {
            for (let i = 0; i < inTableData?.length; i++) {
                if (inTableData[i].value == column) {
                    return inTableData[i].label
                }
            }
        }
        return column
    }

    const hasValidFunction = () => {
        if (type != 'function') return true

        const func = rowData.source
        if (!func) return false

        let found = false
        for (let i = 0; i < classMethods.length; i++) {
            if (classMethods[i] == func) {
                found = true
                break
            }
        }

        return found
    }

    function renderEditableField() {
        //console.log('renderEditableField: ', rowData.edit[rowData.type], getLabel(rowData.edit[rowData.type]))
        if (type == 'source')
            return (
                <SelectPicker
                    className="source-selectpicker"
                    defaultValue={rowData.edit[rowData.type] || ''}
                    data={inTableData}
                    cleanable={false}
                    searchable={false}
                    placeholder={t('Select source ...')}
                    onSelect={(value) => {
                        handleChange(type, value)
                    }}
                />
            )
        if (type == 'fixed-value')
            return (
                <Input
                    className="source-input"
                    defaultValue={rowData.edit[rowData.type]}
                    onChange={(value) => {
                        handleChange(type, value)
                    }}
                />
            )
        if (type == 'function')
            return (
                <SelectPicker
                    className="source-selectpicker"
                    defaultValue={rowData.edit[rowData.type] || ''}
                    data={classMethodsData}
                    cleanable={false}
                    searchable={false}
                    placeholder={t('Select function ...')}
                    onSelect={(value) => {
                        handleChange(type, value)
                    }}
                />
            )

        // return (
        //     <>
        //         <Button
        //             appearance="ghost"
        //             onClick={() => { setMaximized && setMaximized(!maximized) }}
        //             title={maximized ? t('Exit fullsize') : t('Fullsize')}
        //         >
        //             {maximized ? <MinimizeIcon /> : <MaximizeIcon />}
        //         </Button>
        //     </>
        // )

        return null
    }

    function renderStaticField() {
        // Nothing assigned, but the memory has seen this field before. Offered,
        // not filled in: what is written here is a proposal until somebody
        // takes it.
        if (!type && suggestion?.best) {
            return (
                <span className="table-content-edit-span" title={suggestionReason(suggestion)}>
                    <span style={{ opacity: 0.55, fontStyle: 'italic' }}>{suggestion.best.sourceLabel}</span>
                    {canEdit && (
                        <IconButton
                            className="save"
                            icon={<SaveIcon />}
                            title={t('Use this assignment')}
                            appearance="subtle"
                            size="xs"
                            style={{ marginLeft: '0.75em' }}
                            onClick={() => applySuggestion(rowData._id)}
                        />
                    )}
                </span>
            )
        }

        const isValid = hasValidFunction()
        const classNames = ['table-content-edit-span']
        if (type == 'function' && !isValid) classNames.push('invalid-function')

        return (
            <span className={classNames.join(' ')} title={isValid ? '' : t('Function not exist in class.')}>
                {getLabel(rowData[dataKey])}
            </span>
        )
    }

    return (
        <Table.Cell {...props} className={className}>
            {editing ? renderEditableField() : renderStaticField()}
        </Table.Cell>
    )
}
const ActionCell = ({ rowData, dataKey, onClick, className, ...props }: ActionCellProps) => {
    const { canEdit } = useConverter()

    return (
        <Table.Cell {...props} className={className} align="center">
            {canEdit ? (
                rowData.status === 'EDIT' ? (
                    <>
                        <IconButton
                            className="save"
                            icon={<SaveIcon />}
                            title={t('Save')}
                            onClick={() => onClick(rowData._id, 'save')}
                        />
                        <IconButton
                            className="cancel"
                            icon={<CancelIcon />}
                            title={t('Cancel')}
                            onClick={() => onClick(rowData._id, 'cancel')}
                        />
                    </>
                ) : (
                    <Button style={{ width: '130px' }} appearance="link" onClick={() => onClick(rowData._id, 'edit')}>
                        {t('Edit')}
                    </Button>
                )
            ) : (
                <></>
            )}
        </Table.Cell>
    )
}

const TopNavigation = () => {
    const {
        tab,
        data,
        canEdit,
        canEditCode,
        suggestions,
        applyAllSuggestions,
        openSettings,
        setOpenSettings,
        openEditor,
        setOpenEditor,
        openSqlQueryBuilder,
        setOpenSqlQueryBuilder,
        setBuilderTried,
        handleEditorValidation,
        handleSave,
        saveCode,
    } = useConverter()

    // settings handling

    function toggleSettings(): void {
        setOpenSettings((openSettings: boolean) => !openSettings)
    }

    function handleActionSettingsClick(mode: string) {
        //console.log('handleActionEditorClick: ', mode)
        if (mode == 'save') {
            // if (!handleSettingsValidation()) {
            //     // const msg = t('Check your code!') + "\r\n"
            //     //     + t('With errors the code can not be saved.')
            //     // alert(msg)
            //     return
            // }
            handleSave('settings', { settings: data.settings })
        }
        setOpenSettings(false)
    }

    // code editor handling

    function toggleEditor(): void {
        setOpenEditor((openEditor: boolean) => !openEditor)
    }

    function handleActionEditorClick(mode: string) {
        //console.log('handleActionEditorClick: ', mode)
        if (mode == 'save') {
            if (!handleEditorValidation()) {
                // const msg = t('Check your code!') + "\r\n"
                //     + t('With errors the code can not be saved.')
                // alert(msg)
                return
            }
            saveCode()
        }
        setOpenEditor(false)
    }

    // sql query builder handling

    function toggleSqlBuilder(): void {
        setOpenSqlQueryBuilder((openSqlQueryBuilder: boolean) => !openSqlQueryBuilder)
    }

    function handleActionBuilderClick(mode: string) {
        console.log('handleActionBuilderClick: ', mode, data)
        if (mode == 'save') {
            // stored only when every row is right (operator, 14.10.2026); the
            // builder stays open and names the rows — the server checks the same
            const ownFields = data?.inTable ? (data.inTable.mapping ?? []).map((m: any) => m.field) : null
            if (queryRefusal(data.query ?? {}, data.name) || filterRefusals(data.query ?? {}, data.name, ownFields).length) {
                setBuilderTried(true)
                return
            }
            handleSave('query', { query: data.query })
        }
        setBuilderTried(false)
        setOpenSqlQueryBuilder(false)
    }

    // render top navigation

    function renderNavigation() {
        if (openSettings)
            return (
                <>
                    <span className="title">{t('Settings')}</span>
                    <BreadCrumb config={tab} />
                    <span className="actions">
                        {canEdit && (
                            <IconButton
                                className="save"
                                icon={<SaveIcon />}
                                title={t('Save')}
                                onClick={() => handleActionSettingsClick('save')}
                                appearance="subtle"
                            />
                        )}
                        <IconButton
                            className="cancel"
                            icon={<CancelIcon />}
                            title={t('Cancel')}
                            onClick={() => handleActionSettingsClick('cancel')}
                            appearance="subtle"
                        />
                    </span>
                </>
            )

        if (openEditor)
            return (
                <>
                    <span className="title">{t('Code editor')}</span>
                    <BreadCrumb config={tab} />
                    <span className="actions">
                        {canEditCode && (
                            <IconButton
                                className="save"
                                icon={<SaveIcon />}
                                title={t('Save')}
                                onClick={() => handleActionEditorClick('save')}
                                appearance="subtle"
                            />
                        )}
                        <IconButton
                            className="cancel"
                            icon={<CancelIcon />}
                            title={t('Cancel')}
                            onClick={() => handleActionEditorClick('cancel')}
                            appearance="subtle"
                        />
                    </span>
                </>
            )

        if (openSqlQueryBuilder)
            return (
                <>
                    <span className="title">{t('Query Builder')}</span>
                    <BreadCrumb config={tab} />
                    <span className="actions">
                        {canEdit && (
                            <IconButton
                                className="save"
                                icon={<SaveIcon />}
                                title={t('Save')}
                                onClick={() => handleActionBuilderClick('save')}
                                appearance="subtle"
                            />
                        )}
                        <IconButton
                            className="cancel"
                            icon={<CancelIcon />}
                            title={t('Cancel')}
                            onClick={() => handleActionBuilderClick('cancel')}
                            appearance="subtle"
                        />
                    </span>
                </>
            )

        const offers = Object.values(suggestions).filter((entry) => entry.best).length

        return (
            <>
                {canEdit && offers > 0 && (
                    <Button
                        appearance="subtle"
                        onClick={applyAllSuggestions}
                        title={t('Fills every empty field the memory recognises. Nothing already assigned is touched.')}
                    >
                        {t('Assign {count} automatically').replace('{count}', String(offers))}
                    </Button>
                )}
                <Button appearance="subtle" onClick={toggleEditor} title={t('Code Editor')}>
                    <CodeIcon />
                </Button>
                <Button appearance="subtle" onClick={toggleSqlBuilder} title={t('Query Builder')}>
                    {/* <SvgLoader file="/images/query.svg" style={{ lineHeight: 1 }} /> */}
                    <QueryIcon />
                </Button>
                <Button appearance="subtle" onClick={toggleSettings} title={t('Settings')}>
                    <SettingsIcon />
                </Button>
            </>
        )
    }

    return <Container className="converterlist-top-navigation-actions">{renderNavigation()}</Container>
}

export default function EditConverterList({ tabId }: EditConverterListProps) {
    const tab = findTab(tabId)
    if (!tab) return null // or throw an error
    const converterId = String(tab?.store?.data?.id || '')
    const [reloadCounter, setReloadCounter] = useState<number>(0)
    const { data, loading, error } = useQuery(converterListModel.read, {
        args: {
            params: [project.currentProject.value?._id, tab.store?.data?.outTableId],
        },
        allowEmptyVariables: false,
        refreshCounter: `${tab.store?.data?.outTableId}:${reloadCounter}` as any,
    })
    const [rowData, setRowData] = useSyncedState(tab, 'rowData', [])
    const [openSettings, setOpenSettings] = useState<boolean>(false)
    const [openEditor, setOpenEditor] = useState<boolean>(false)
    const [openSqlQueryBuilder, setOpenSqlQueryBuilder] = useState<boolean>(false)
    const [editorDataHasChanged, setEditorDataHasChanged] = useState<number>(0)
    const [_settingsHasChanged, setSettingsHasChanged] = useState<number>(0)
    const [_sqlQueryBuilderHasChanged, setSqlQueryBuilderHasChanged] = useState<number>(0)
    const [builderTried, setBuilderTried] = useState<boolean>(false)
    const [classMethods, setClassMethods] = useState<string[]>([])
    const [classMethodsData, setClassMethodsData] = useState<LabelValueProps[]>([])
    const [searchTargetField, setSearchTargetField] = useState<string>('')
    const [searchType, setSearchType] = useState<classMethodsDataProps>()
    const [info, setInfo] = useState<BuildInfoType | null>(null)
    const topOffset = useRef<number>(undefined)

    //
    // The functions as they were when the editor was opened.
    //
    // A ref, not state: nothing on screen depends on it, and it must not be
    // rewritten as the user types — that is exactly what makes it survive
    // intermediate steps. Renaming a to b and then b to c asks one question
    // (a -> c), not two.
    //
    const methodsAtOpen = useRef<TMethodSnapshot | null>(null)
    const [renameOffers, setRenameOffers] = useState<TRenameOffer[]>([])

    //
    // One person edits a converter at a time.
    //
    // The mechanism is the same one project, tenant and user already use. This
    // editor never had it, so two people on the same converter overwrote each
    // other's field assignments without either of them noticing.
    //
    // The whole converter is held, not the single row: the code, the query and
    // the settings live in this editor too, and whoever changes those changes
    // the ground the field assignments stand on.
    //
    const reloadEditor = () => {
        // The rows live in the tab and are only filled while empty, so they have
        // to go before the refetch — otherwise a session that just took over
        // keeps showing what it read before the previous one saved.
        setRowData([])
        setReloadCounter((count) => count + 1)
    }

    const lock = useEditLock({
        table: 'converter',
        url: 'converter',
        id: converterId,
        tabId,
        userId: String(user.value?._id || ''),
        userName: String(user.value?.userName || ''),
        enabled: Boolean(converterId),
        // Only when there was something to go stale. On a freshly opened tab the
        // rows are empty and the normal load is already on its way.
        onAcquire: () => {
            if (rowData.length) reloadEditor()
        },
        onUpdated: reloadEditor,
    })

    const canEdit = hasPermission(Capabilities.convert_write) && lock.status === 'owned'
    // Same lock, different right: the editor's content is already gated on
    // converter_code_write (CodeEditor.tsx), and its Save button has to agree —
    // otherwise it offers an action the server answers with 403.
    const canEditCode = hasPermission(Capabilities.converter_code_write) && lock.status === 'owned'

    //
    // What the memory offers for the fields nobody has assigned yet.
    //
    // Kept by target key so a row can look up its own without searching. An
    // empty answer is the normal case at the beginning: nothing is invented, so
    // there is nothing to show until somebody has assigned these fields once.
    //
    const [suggestions, setSuggestions] = useState<Record<string, TSuggestion>>({})

    const loadSuggestions = async () => {
        if (!converterId) return

        const res: any = await converterListModel.mappingSuggestions(
            project.currentProject.value?._id || '',
            converterId
        )
        const list: TSuggestion[] = Array.isArray(res?.data) ? res.data : []

        setSuggestions(Object.fromEntries(list.map((entry) => [entry.target, entry])))
    }

    useEffect(() => {
        const _ = async () => {
            const _info = await getBuildInfo()
            setInfo(_info)

            let top = _info.isDaily || _info.isDemo ? 0.5 : 0
            if (_info.isDaily) top += 1.5
            if (_info.isDemo) top += 1.5
            topOffset.current = top
        }
        _()
    }, [])

    const inTableData = useMemo(() => {
        const own = data?.inTable?.mapping?.map((item: any) => ({ value: item.column, label: item.field })) ?? []
        // and the fields the query builder projects from joined tables — a
        // lookup table or another template — under "Table.Field"
        const joined = (data?.query?.projections ?? []).map((field: string) => ({ value: field, label: field }))
        return [...own, ...joined]
    }, [data?.inTable, data?.query?.projections, tab.store?.data?.outTableId])

    if (!tab.store?.data?.showInTables) {
        tab.store.data.showInTables = true
    }
    useEffect(() => {
        const converterId = String(tab?.store?.data?.id || '')
        if (!converterId) return

        // Global close-on-delete (LayoutManager closes by resourceId match)
        tab.store.resourceId = converterId
        tab.store.table = 'converter'
    }, [tab])

    // fetch all wrapper methods from the code script
    useEffect(() => {
        const methods = getClassMethods(data?.code)
        if (methods.toString() != classMethods.toString()) {
            setClassMethods(methods)
            setClassMethodsData(methods.map((item: string) => ({ label: item, value: item })))
        }
    }, [data?.code, editorDataHasChanged])

    // // handle sql query builder
    // useEffect(() => {
    //     if (data?.query) {
    //         console.log('SqlQueryBuilder has changed', data?.query, sqlQueryBuilderHasChanged)
    //     }
    // }, [data?.query, sqlQueryBuilderHasChanged])

    // // handle converter settings
    // useEffect(() => {
    //     if (data?.query) {
    //         console.log('Converter settings has changed', data?.query, settingsHasChanged)
    //     }
    // }, [data?.query, settingsHasChanged])

    // fetch all out tables from this project
    useEffect(() => {
        if (data && data.mapping && rowData.length === 0) {
            setRowData(data.mapping.map((item: any) => ({ ...item, status: null })))
        }
    }, [data])

    // Once the converter is loaded, and again after every assignment: taking a
    // source column changes what is left for the others.
    useEffect(() => {
        if (data) void loadSuggestions()
    }, [data, converterId])

    // Taken when the editor opens, so a rename is measured against where the
    // user started rather than against the last keystroke.
    //
    // ⚠️ Has to live above the early returns below — a hook after a conditional
    // return is a hook that sometimes does not run, and React counts them.
    useEffect(() => {
        if (!openEditor) return
        methodsAtOpen.current = snapshotMethods(String(data?.code ?? ''))
    }, [openEditor])

    if (error) return <PanelError message={error.message} />
    if (loading) return <Loading />

    // both handling

    const handleSave = (type: string, data: any) => {
        //console.log(type, data)
        const newData = { ...data, ...{ _type: type } }
        return converterListModel.update(project.currentProject.value?._id || '', tab.store?.data.id, newData)
    }

    //
    // Renamed and removed functions
    // -----------------------------
    //
    // A mapping rule of type 'function' stores a method NAME. Rename the method
    // and the rule points at nothing: it saves without complaint, and at
    // conversion time every table row produces an error instead of a value.
    // Checked only in the other direction until now — saving a RULE verifies the
    // function exists, saving the CODE verified nothing.
    //
    const snapshotMethods = (code: string): TMethodSnapshot => ({
        names: getClassMethods(code) ?? [],
        bodies: getClassMethodBodies(code) ?? {},
    })

    /**
     * Saves the code and only then asks about what it orphaned.
     *
     * ⚠️ The order is the point. The code is valid and belongs on disk; tying
     * the save to an answer would mean somebody loses their work by dismissing a
     * question they did not expect.
     */
    const saveCode = async () => {
        const before = methodsAtOpen.current
        await handleSave('source', { code: data.code })

        const after = snapshotMethods(String(data?.code ?? ''))
        // Straight away, and regardless of what the dialog does: otherwise the
        // next save reports the same rename again, long after it was dealt with.
        methodsAtOpen.current = after

        if (!before) return

        const offers = buildRenameOffers(before, after, rowData, getLabel)
        // Nothing orphaned, no dialog. A box that appears when there is nothing
        // to decide gets dismissed by reflex before it is ever right.
        if (offers.length) setRenameOffers(offers)
    }

    /**
     * Points the confirmed rules at their new function.
     *
     * Through the ordinary field PATCH, which verifies that the function exists
     * — it does by now, the code was saved first. An entry left empty is a
     * deliberate "leave it alone" and is skipped.
     */
    const applyRenames = async (assignments: Record<string, string>) => {
        setRenameOffers([])

        const next: any = Object.assign([], rowData)
        let applied = 0

        for (const [rowId, functionName] of Object.entries(assignments)) {
            if (!functionName) continue

            const row = next.find((item: any) => String(item._id) === rowId)
            if (!row) continue

            row.source = functionName
            endRowEdit(row)

            await handleSave('field', row)
            applied++
        }

        if (!applied) return

        setRowData(next)
        toastMessage(t('{count} field(s) now use the new function').replace('{count}', String(applied)))
    }

    // code editor handling

    function handleEditorValidation() {
        return !tab?.store.errors || tab.store.errors.length == 0
    }

    // field edit handling

    const handleFieldValidation = (activeItem: any) => {
        if (!activeItem) return false
        if (activeItem.type == 'function') {
            if (!activeItem.edit.function) return false
            if (classMethods.indexOf(activeItem.edit.function) == -1) return false
        }
        return true
    }

    const handleChange = (id: string, key: string, value: any, useEdit = false) => {
        const nextData: any = Object.assign([], rowData)
        const activeItem = nextData.find((item: any) => item._id === id)
        if (activeItem) {
            if (useEdit) activeItem.edit[key] = value
            else activeItem[key] = value
        }
        setRowData(nextData)
    }

    /**
     * Takes the offer for one row.
     *
     * Saved through the same path as a hand-made assignment, so the memory
     * counts it like one — accepting a suggestion is a decision too.
     */
    const applySuggestion = (rowId: string) => {
        const next: any = Object.assign([], rowData)
        const row = next.find((item: any) => item._id === rowId)
        const offer = row && suggestions[String(row.target)]
        if (!row || row.type || !offer?.best) return

        row.type = 'source'
        row.source = offer.best.source
        endRowEdit(row)

        setRowData(next)
        handleSave('field', row)
        void loadSuggestions()
    }

    /** Every offer at once. Fills gaps only — it never overrides a decision. */
    const applyAllSuggestions = () => {
        const next: any = Object.assign([], rowData)
        let applied = 0

        for (const row of next) {
            const offer = suggestions[String(row.target)]
            if (row.type || !offer?.best) continue

            row.type = 'source'
            row.source = offer.best.source
            endRowEdit(row)
            handleSave('field', row)
            applied += 1
        }

        setRowData(next)
        void loadSuggestions()

        toastMessage(
            applied
                ? t('{count} of {total} fields assigned')
                      .replace('{count}', String(applied))
                      .replace('{total}', String(next.length))
                : t('Nothing to assign — no field is known from an earlier mapping.')
        )
    }

    const handleEditState = (id: string, mode: string) => {
        const nextData: any = Object.assign([], rowData)
        const activeItem: any = nextData.find((item: any) => item._id === id)
        if (activeItem) {
            if (mode == 'edit') {
                beginRowEdit(activeItem, MAPPING_EDIT_FIELDS)
                converterTypes.forEach((item) => {
                    if (item.value) activeItem.edit[item.value] = activeItem.type == item.value ? activeItem.source : ''
                })
            } else if (mode == 'save') {
                if (handleFieldValidation(activeItem)) {
                    activeItem.source = activeItem.edit[activeItem.type] || ''
                    endRowEdit(activeItem)
                    handleSave('field', activeItem)
                }
            } /*if (mode == 'cancel')*/ else {
                const loadedItem: any = data.mapping.find((item: any) => item._id === id)
                cancelRowEdit(activeItem, loadedItem, MAPPING_EDIT_FIELDS)
            }
        }
        setRowData(nextData)
    }
    const filterTable = () => {
        let filteredRows: any = [...rowData]
        if (!searchType && !searchTargetField) {
            return filteredRows
        } else {
            if (searchTargetField) {
                filteredRows = filteredRows.filter((row: any) =>
                    getLabel(row.target).toLowerCase().includes(searchTargetField.toLowerCase())
                )
            }
            if ((searchType as any) == 'all') {
                return filteredRows
            }
            if ((searchType as any) == 'not-assigned') {
                return filteredRows.filter((row: any) => !row.type)
            }
            if (searchType) {
                filteredRows = filteredRows.filter((row: any) => row.type == searchType)
            }

            return filteredRows
        }
    }

    const getLabel = (column: string) => {
        for (let i = 0; i < data.outTable.mapping.length; i++) {
            if (data.outTable.mapping[i].column == column) {
                return data.outTable.mapping[i].field
            }
        }
        return column
    }

    const value: EditorContextValue = {
        tab,
        data,
        canEdit,
        canEditCode,
        suggestions,
        applySuggestion,
        applyAllSuggestions,
        classMethods,
        classMethodsData,
        rowData,
        openSettings,
        setOpenSettings,
        setSettingsHasChanged,
        openEditor,
        setOpenEditor,
        setEditorDataHasChanged,
        openSqlQueryBuilder,
        setOpenSqlQueryBuilder,
        setSqlQueryBuilderHasChanged,
        builderTried,
        setBuilderTried,
        handleEditorValidation,
        handleSave,
        saveCode,
    }

    return (
        <EditorContext.Provider value={value}>
            <Container className="mapping-list-wrapper">
                <Container
                    className={
                        'converterlist-top-navigation' +
                        (openEditor || openSqlQueryBuilder || openSettings ? ' fullsize' : '')
                    }
                    // style={{ '--top-offset': `${topOffset.current}em !important` }}
                    style={{ '--top-offset': `${topOffset.current}em` } as React.CSSProperties}
                >
                    <TopNavigation />
                    {openSettings && <ConverterSettings />}
                    {openEditor && <CodeEditor />}
                    {openSqlQueryBuilder && <SQLQueryBuilder />}
                </Container>

                {/* Only ever after a save that actually orphaned something —
                    buildRenameOffers returns an empty list otherwise, and an
                    empty list never gets here. */}
                {renameOffers.length > 0 && (
                    <RenameFunctionsDialog
                        offers={renameOffers}
                        methods={classMethodsData}
                        onApply={applyRenames}
                        onCancel={() => setRenameOffers([])}
                    />
                )}

                {lock.banner}

                <Container className="search-container">
                    <InputGroup className="search-bar">
                        <Input placeholder={t('Target field')} onChange={setSearchTargetField} />
                        <InputGroup.Addon>
                            <SearchIcon />
                        </InputGroup.Addon>
                    </InputGroup>
                    <SelectPicker
                        className="search-select"
                        onSelect={setSearchType}
                        defaultValue="all"
                        data={converterTypesSearchOptions}
                        cleanable={false}
                        searchable={false}
                        placeholder={t('Type')}
                    />
                </Container>
                <div className="table-shell">
                    <Table
                        data={filterTable()}
                        loading={loading}
                        rowKey="_id"
                        //wordWrap="break-word"
                        showHeader
                        fillHeight
                    >
                        {/* target */}
                        <Table.Column align="left" flexGrow={1} resizable fullText>
                            <Table.HeaderCell className="name">{t('Target field')}</Table.HeaderCell>
                            <Table.Cell className="target" dataKey="target">
                                {(row: any) => getLabel(row.target)}
                            </Table.Cell>
                        </Table.Column>

                        {/* type */}
                        <Table.Column align="left" width={210} resizable>
                            <Table.HeaderCell className="type">{t('Type')}</Table.HeaderCell>
                            <EditableTypeCell className="type" dataKey="type" onChange={handleChange} />
                        </Table.Column>

                        {/* source */}
                        <Table.Column align="left" flexGrow={1.8} resizable fullText>
                            <Table.HeaderCell className="source">{t('Display')}</Table.HeaderCell>
                            <EditableSourceCell
                                className="source"
                                dataKey="source"
                                onChange={handleChange}
                                inTableData={inTableData}
                                //maximized={maximized}
                                //setMaximized={setMaximized}
                                //classMethods={classMethodsData}
                                fullText
                            />
                        </Table.Column>

                        {/* action button */}
                        <Table.Column align="center" width={130}>
                            <Table.HeaderCell>&nbsp;</Table.HeaderCell>
                            <ActionCell className="action" dataKey="_id" onClick={handleEditState} />
                        </Table.Column>
                    </Table>
                </div>
            </Container>
        </EditorContext.Provider >
    )
}
